'use client';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from './I18nProvider';
import { PILL_BUTTON_CLASS } from '@/utils/pillButton';

/**
 * Dropdown lingua, drop-in (legge il contesto da useI18n, nessuna prop
 * obbligatoria). Passa locale/locales/onChange espliciti solo se serve
 * usarlo fuori da un I18nProvider (es. storybook, preview isolata).
 *
 * @param {{ locale?: string, locales?: Array<{code:string,label:string,flag?:string}>, onChange?: (code:string)=>void, className?: string }} props
 */
export function LanguageSwitcher({ locale: locProp, locales: locsProp, onChange: onChangeProp, className = '' }) {
  const ctx = useI18nSafe();
  const locale   = locProp  ?? ctx?.locale;
  const locales  = locsProp ?? ctx?.locales ?? [];
  const onChange = onChangeProp ?? ctx?.setLocale ?? (() => {});

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const current = locales.find((l) => l.code === locale);

  useEffect(() => {
    if (!open) return undefined;
    function handleClickOutside(e) {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  function toggleOpen() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen((o) => !o);
  }

  if (locales.length === 0) return null;

  return (
    <div className={['relative', className].join(' ')}>
      <button
        ref={btnRef}
        onClick={toggleOpen}
        title="Cambia lingua"
        className={PILL_BUTTON_CLASS + ' text-xs font-medium'}
      >
        {current ? current.code.toUpperCase() : '—'}
      </button>
      {open && pos && createPortal(
        // Portalato su <body> invece che annidato nell'header: un header
        // "overlay" (sopra un hero scuro) sovrascrive --color-text-muted/
        // --color-border in chiaro per il proprio testo, e quella custom
        // property si propaga per cascata a QUALSIASI discendente DOM anche
        // se posizionato altrove (l'`absolute` non c'entra, l'ereditarietà
        // segue l'albero DOM). Il pannello ha sempre lo stesso sfondo chiaro
        // fisso (bg-theme-bg-surface): ereditare testo forzato "chiaro"
        // dall'header lo rendeva bianco su bianco, illeggibile — bug reale
        // scoperto in uso. Portalarlo rompe l'ereditarietà indesiderata,
        // restando comunque dentro <html data-theme/data-mode> per il resto
        // dei token colore.
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
          className="z-[1100] flex flex-col overflow-hidden rounded-[12px] border border-theme-border bg-theme-bg-surface shadow-vs-md"
        >
          {locales.map((l) => (
            <button
              key={l.code}
              onClick={() => { onChange(l.code); setOpen(false); }}
              className={[
                'whitespace-nowrap px-3 py-1.5 text-left text-xs transition-colors hover:bg-theme-bg-subtle',
                l.code === locale ? 'text-theme-accent' : 'text-theme-text-muted',
              ].join(' ')}
            >
              {l.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// useI18n lancia se non c'è un provider — qui serve poterlo usare anche fuori,
// quindi lo intercettiamo senza propagare l'errore.
function useI18nSafe() {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useI18n();
  } catch {
    return null;
  }
}
